import { Card, Typography, Button, Space, Tooltip, Switch } from "antd";
import { Bar, Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,ArcElement,Title as ChartTitle} from "chart.js";
import { ExpandOutlined, BarChartOutlined, PieChartOutlined, PercentageOutlined,PictureOutlined,FileExcelOutlined} from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  ChartTooltip,
  Legend,
  ChartDataLabels,
  ChartTitle
);

const { Title } = Typography;

export function IntentosValidacionChart({ data = [], efectividadData = [] }) {
  const [viewMode, setViewMode] = useState('count');
  const [chartType, setChartType] = useState('bar');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEfectividad, setShowEfectividad] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    if (showEfectividad) {
      setViewMode('percentage');
    }
  }, [showEfectividad]);

 const transformarDatosIntentos = (datos) => {
  if (!datos || datos.length === 0) return [];

  return datos.map(item => {
    let nuevoLabel;

    if (item.total_intentos === 1) {
      nuevoLabel = '1 intento';
    } else {
      nuevoLabel = `${item.total_intentos} intentos`;
    }

    return {
      ...item,
      label: nuevoLabel
    };
  });
};


  const transformarDatosEfectividad = (datos) => {
  if (!datos || datos.length === 0) return [];

  return datos.map(item => {
    let nuevoLabel;

    if (item.total_intentos === 1) {
      nuevoLabel = '1 intento';
    } else {
      nuevoLabel = `${item.total_intentos} intentos`;
    }

    return {
      ...item,
      label: nuevoLabel
    };
  });
};

  const datosTransformados = transformarDatosIntentos(data);
  const efectividadTransformada = transformarDatosEfectividad(efectividadData);
  if (!datosTransformados || datosTransformados.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Intentos de Validación</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }
  const PALETA_BODECOM = [
    "#024589ff",
    "#e9af0eff",
    "#20b325ff",
    "#ff6b6b",
    "#5c6bc0",
    "#26c6da"
  ];

  function getColor(index) {
    return PALETA_BODECOM[index % PALETA_BODECOM.length];
  }
  const totalRegistros = datosTransformados.reduce((sum, item) => sum + item.total, 0);
  const labels = showEfectividad && efectividadTransformada.length > 0 
    ? efectividadTransformada.map(item => item.label)
    : datosTransformados.map(item => item.label);
  const getDataValues = () => {
    if (showEfectividad && efectividadTransformada.length > 0) {
      return efectividadTransformada.map(item => parseFloat(item.efectividad));
    } else {
      if (viewMode === 'count') {
        return datosTransformados.map(item => item.total);
      } else {
        return datosTransformados.map(item => 
          totalRegistros > 0 ? ((item.total / totalRegistros) * 100).toFixed(1) : 0
        );
      }
    }
  };

  const getDatasetLabel = () => {
    if (showEfectividad) {
      return 'Tasa de Efectividad (%)';
    } else {
      return viewMode === 'count' ? 'Número de Registros' : 'Porcentaje (%)';
    }
  };
  const exportHighQualityImage = () => {
    if (!chartRef.current) {
      console.error('No se pudo encontrar el gráfico');
      return;
    }
    const canvas = chartRef.current.canvas;
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
    const exportCanvas = document.createElement('canvas');
    const ctx = exportCanvas.getContext('2d');
    exportCanvas.width = originalWidth * 2;
    exportCanvas.height = originalHeight * 2;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.scale(2, 2);
    ctx.drawImage(canvas, 0, 0);
    const image = exportCanvas.toDataURL('image/png', 1.0);
    const link = document.createElement('a');
    link.href = image;
    link.download = `${showEfectividad ? 'efectividad_intentos' : 'intentos_validacion'}_alta_calidad_${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  };

  const handleExportCSV = () => {
    let csvContent;
    if (showEfectividad && efectividadTransformada.length > 0) {
      csvContent = [
        ['Intentos (Real)', 'Intentos (Mostrado)', 'Participantes', 'Exitosos', 'No Exitosos', 'Contactados', 'Efectividad (%)'],
        ...efectividadTransformada.map(item => [
          item.total_intentos - 1, 
          item.total_intentos,      
          item.total_participantes,
          item.exitosos,
          item.no_exitosos,
          item.contactados,
          item.efectividad
        ])
      ];
    } else {
      csvContent = [
        ['Intentos (Real)', 'Intentos (Mostrado)', 'Registros', 'Porcentaje'],
        ...datosTransformados.map(item => [
          item.total_intentos - 1, 
          item.total_intentos,      
          item.total,
          totalRegistros ? ((item.total / totalRegistros) * 100).toFixed(2) + '%' : '0%'
        ])
      ];
    }

    const csvString = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = showEfectividad 
      ? `efectividad_intentos_${new Date().toISOString().split('T')[0]}.csv`
      : `intentos_validacion_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const barChartData = {
    labels: labels,
    datasets: [{
      label: getDatasetLabel(),
      data: getDataValues(),
      backgroundColor: labels.map((_, index) => getColor(index)),
      borderRadius: 8,
      borderWidth: 1,
    }]
  };

  const pieChartData = {
    labels: labels,
    datasets: [{
      data: getDataValues(),
      backgroundColor: labels.map((_, index) => getColor(index)),
      borderWidth: 1,
    }]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grace: '5%',
        title: {
          display: true,
          text: getDatasetLabel(),
          font: {
            size: 12
          }
        },
        ticks: {
          callback: function(value) {
            if (showEfectividad) {
              return value + '%';
            }
            return value;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Número de Intentos',
          font: {
            size: 12
          }
        }
      }
    },
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 20
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const label = context.label;
            if (showEfectividad) {
              return `${label}: ${value}% de efectividad`;
            } else if (viewMode === 'count') {
              const pct = totalRegistros ? ((value / totalRegistros) * 100).toFixed(1) : 0;
              return `${label}: ${value} registros (${pct}%)`;
            } else {
              return `${label}: ${value}%`;
            }
          }
        }
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: 0,
        color: '#000',
        font: { 
          size: 11,
          weight: 'bold'
        },
        formatter: (value, context) => {
          if (showEfectividad) {
            return `${value}%`;
          } else if (viewMode === 'count') {
            const pct = totalRegistros ? ((value / totalRegistros) * 100).toFixed(1) : 0;
            return `${value} (${pct}%)`;
          } else {
            return `${value}%`;
          }
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const label = context.label;
            if (showEfectividad) {
              return `${label}: ${value}% de efectividad`;
            } else if (viewMode === 'count') {
              const pct = totalRegistros ? ((value / totalRegistros) * 100).toFixed(1) : 0;
              return `${label}: ${value} registros (${pct}%)`;
            } else {
              return `${label}: ${value}%`;
            }
          }
        }
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 11,
          weight: 'bold'
        },
        formatter: (value, context) => {
          if (showEfectividad) {
            return `${value}%`;
          } else if (viewMode === 'count') {
            const pct = totalRegistros ? ((value / totalRegistros) * 100).toFixed(1) : 0;
            return `${value}\n(${pct}%)`;
          } else {
            return `${value}%`;
          }
        }
      }
    }
  };

  return (
    <Card 
      hoverable 
      style={{ 
        borderRadius: 12,
        ...(isExpanded && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          margin: 20,
          backgroundColor: 'white'
        })
      }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={4} style={{ 
            margin: 0, 
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            {showEfectividad ? 'Efectividad por Intentos' : 'Intentos de Validación'}
          </Title>
          <div style={{ 
            fontSize: 14, 
            color: '#666',
            marginTop: 4
          }}>
            {showEfectividad 
              ? `Analizando ${efectividadTransformada.length || 0} tipos de intentos` 
              : `${totalRegistros || 0} registros totales`
            }
          </div>
        </div>
        <Space wrap>
          <Tooltip title={showEfectividad ? "Mostrar número de registros" : "Mostrar efectividad"}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8,
              padding: '4px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              border: '1px solid #dee2e6'
            }}>
              <span style={{ 
                fontSize: 13, 
                color: showEfectividad ? '#6c757d' : '#024589ff',
                fontWeight: showEfectividad ? 'normal' : '600'
              }}>
                Registros
              </span>
              <Switch 
                checked={showEfectividad}
                onChange={setShowEfectividad}
                size="small"
              />
              <span style={{ 
                fontSize: 13, 
                color: showEfectividad ? '#024589ff' : '#6c757d',
                fontWeight: showEfectividad ? '600' : 'normal'
              }}>
                Efectividad
              </span>
            </div>
          </Tooltip>
          <div style={{ display: 'flex', gap: 8 }}>
            {!showEfectividad && (
              <Tooltip title="Alternar vista: Número de registros / Porcentaje">
                <Button 
                  icon={viewMode === 'count' ? <PercentageOutlined /> : <BarChartOutlined />}
                  onClick={() => setViewMode(viewMode === 'count' ? 'percentage' : 'count')}
                  size="small"
                  type={viewMode === 'count' ? 'default' : 'dashed'}
                >
                  {viewMode === 'count' ? 'Porcentaje' : 'Números'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Cambiar tipo de gráfico">
              <Button 
                icon={chartType === 'bar' ? <PieChartOutlined /> : <BarChartOutlined />}
                onClick={() => setChartType(chartType === 'bar' ? 'pie' : 'bar')}
                size="small"
                type={chartType === 'bar' ? 'default' : 'dashed'}
              >
                {chartType === 'bar' ? 'Pastel' : 'Barras'}
              </Button>
            </Tooltip>
            <Tooltip title="Exportar imagen del gráfico (alta calidad)">
              <Button 
                icon={<PictureOutlined />}
                onClick={exportHighQualityImage}
                size="small"
                type="primary"
              >
                Imagen
              </Button>
            </Tooltip>
            <Tooltip title="Exportar datos en formato CSV">
              <Button 
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                size="small"
              >
                CSV
              </Button>
            </Tooltip>
            <Tooltip title={isExpanded ? "Minimizar" : "Maximizar"}>
              <Button 
                icon={<ExpandOutlined />}
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                type={isExpanded ? "primary" : "default"}
              />
            </Tooltip>
          </div>
        </Space>
      </div>
      <div style={{ 
        height: isExpanded ? 'calc(100vh - 200px)' : 400,
        backgroundColor: '#FFFFFF'
      }}>
        {chartType === 'bar' ? (
          <Bar 
            ref={chartRef}
            data={barChartData} 
            options={{
              ...barChartOptions,
              backgroundColor: '#FFFFFF'
            }} 
          />
        ) : (
          <Pie 
            ref={chartRef}
            data={pieChartData} 
            options={{
              ...pieChartOptions,
              backgroundColor: '#FFFFFF'
            }}
          />
        )}
      </div>
    </Card>
  );
}